import JsonLd from "@/components/JsonLd.tsx";
import JsonLdBreadcrumbList from "@/components/JsonLdBreadcrumbList.tsx";
import {config} from "@/config/config.ts";
import FaqComponent from "@/landing/sections/FaqComponent.tsx";
import {createFileRoute, Link} from '@tanstack/react-router'
import LandingNav from "../landing/components/LandingNav.tsx";
import ArrowUpRightIcon from "../landing/icons/ArrowUpRightIcon.tsx";
import CheckBadgeIcon from "../landing/icons/CheckBadgeIcon.tsx";
import FooterSection from "../landing/sections/FooterSection.tsx";

export const Route = createFileRoute('/esports-league-software')({
    component: RouteComponent,
})

const FEATURES = [
    {code: "SP", title: "Seasons & splits", desc: "Run a split over weeks with a fixed schedule and a live table."},
    {code: "BO", title: "Best-of series", desc: "Record Bo1, Bo3 and Bo5 series map by map, not just the winner."},
    {code: "RS", title: "Team rosters", desc: "Keep rosters and substitutes attached to each team across the season."},
    {code: "SW", title: "Swiss rounds", desc: "Pair teams on record when a full round-robin is too many matches."},
    {code: "TR", title: "Tiers & divisions", desc: "Run open, intermediate and invite divisions with their own tables."},
    {code: "RK", title: "Standings & rankings", desc: "Publish records, map differential and rankings as results land."},
];

const LEAGUE_TYPES = [
    {name: "Season / Split", sub: "Everyone plays everyone", to: "/round-robin-tournament-generator/"},
    {name: "Swiss League", sub: "Paired on record each round", to: "/swiss-system-tournament-generator/"},
    {name: "Esports Tournaments", sub: "Group stage and knockout brackets", to: "/esports-tournament-software/"},
    {name: "Sports League Management", sub: "Recurring fixtures and standings", to: "/sports-league-management-software/"},
];

const BENEFITS = [
    {title: "Schedule around your community", desc: "Set match times for the whole split so teams can plan around them."},
    {title: "Series recorded properly", desc: "A 2-1 series keeps its map scores instead of collapsing into one number."},
    {title: "A table nobody argues with", desc: "Standings update from submitted results, visible to every team at once."},
];

const USE_CASES = [
    {title: "For Community Leagues", desc: "Run recurring online seasons for your Discord or community."},
    {title: "For University Esports", desc: "Manage inter-university competition across a term."},
    {title: "For Amateur Circuits", desc: "Run open divisions that feed into higher tiers."},
    {title: "For Game Publishers", desc: "Operate structured seasons for a title's competitive scene."},
    {title: "For Organisations", desc: "Track multiple rosters across different divisions."},
    {title: "For Ladder Play", desc: "Keep a persistent ranking between seasons."},
];

const COMPARE = [
    {need: "Split schedule", manual: "A pinned message that goes stale", scorepal: "A fixed schedule every team can see"},
    {need: "Series results", manual: "Screenshots collected in a channel", scorepal: "Map scores submitted against the fixture"},
    {need: "Standings", manual: "A spreadsheet one admin maintains", scorepal: "Records and differential calculated automatically"},
    {need: "Swiss pairings", manual: "Worked out manually between rounds", scorepal: "Teams paired on record each round"},
    {need: "Team experience", manual: "Teams ask admins who they play", scorepal: "Fixtures and standings on a public page"},
];

const FAQS = [
    {
        q: 'What is esports league software?',
        a: <>Esports league software helps organisers run recurring online competition — building a season or split schedule, collecting series results, and publishing standings and rankings for every division.</>,
    },
    {
        q: 'Can ScorePal record best-of series?',
        a: <>Yes. A series is stored map by map, so a 2-1 Bo3 keeps its individual map scores rather than being reduced to a single result.</>,
    },
    {
        q: 'Does ScorePal support Swiss rounds?',
        a: <>Yes. <Link to={"/swiss-system-tournament-generator/" as any} className="text-[#082F8A] underline font-semibold">Swiss pairing</Link> matches teams on similar records each round, which suits large fields where a full round-robin would mean too many matches.</>,
    },
    {
        q: 'Can we run multiple divisions or tiers?',
        a: <>Yes. Open, intermediate and invite divisions each run as their own competition with their own schedule and table.</>,
    },
    {
        q: 'Is this different from esports tournament software?',
        a: <>Yes. Esports league software is built for a season or split played over weeks. <Link to={"/esports-tournament-software/" as any} className="text-[#082F8A] underline font-semibold">Esports tournament software</Link> is built for one-off cups with a group stage and knockout bracket.</>,
    },
];

function RouteComponent() {
    return (
        <div className="min-h-screen bg-[#EDEEF4]">
            <title>Esports League Software for Organisers & Communities | ScorePal</title>
            <meta name="description" content="Run esports leagues with ScorePal. Manage seasons and splits, best-of series, team rosters, Swiss rounds, divisions and live standings from one platform." />
            <link rel="canonical" href={`${config.publicUrl}/esports-league-software/`} />
            <meta property="og:title" content="Esports League Software | ScorePal" />
            <meta property="og:description" content="Create esports season schedules, record series results and publish standings with ScorePal." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={`${config.publicUrl}/esports-league-software/`} />
            <meta property="og:image" content="https://scorepal.co/images/esports-league-software-og.jpg" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Esports League Software | ScorePal" />
            <meta name="twitter:description" content="Run esports seasons, splits, series results and standings from one platform." />

            <JsonLd id={`page${Route.id}/app`} item={{
                "@context": "https://schema.org",
                "@type": "SoftwareApplication",
                "name": "ScorePal Esports League Software",
                "applicationCategory": "SportsApplication",
                "operatingSystem": "Web",
                "description": "Esports league software for running seasons and splits, best-of series, Swiss rounds, divisions and standings.",
                "url": "https://scorepal.co/esports-league-software/",
                "publisher": {"@type": "Organization", "name": "ScorePal"},
            }} />
            <JsonLd id={`page${Route.id}/faq-schema`} item={{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                "mainEntity": [
                    {"@type": "Question", "name": "What is esports league software?", "acceptedAnswer": {"@type": "Answer", "text": "Esports league software helps organisers run recurring online competition, building a season or split schedule, collecting series results, and publishing standings and rankings for every division."}},
                    {"@type": "Question", "name": "Can ScorePal record best-of series?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. A series is stored map by map, so a 2-1 Bo3 keeps its individual map scores rather than being reduced to a single result."}},
                    {"@type": "Question", "name": "Does ScorePal support Swiss rounds?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Swiss pairing matches teams on similar records each round, which suits large fields where a full round-robin would mean too many matches."}},
                    {"@type": "Question", "name": "Can we run multiple divisions or tiers?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Open, intermediate and invite divisions each run as their own competition with their own schedule and table."}},
                    {"@type": "Question", "name": "Is this different from esports tournament software?", "acceptedAnswer": {"@type": "Answer", "text": "Yes. Esports league software is built for a season or split played over weeks, while esports tournament software is built for one-off cups with a group stage and knockout bracket."}},
                ],
            }} />
            <JsonLdBreadcrumbList routeId={Route.id} crumbs={[{name: "Esports League Software", item: "https://scorepal.co/esports-league-software/"}]} />

            {/* Hero */}
            <section className="relative overflow-hidden p-4">
                <div className="rounded-4xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] pb-12">
                    <div className="relative z-20">
                        <LandingNav />
                    </div>

                    <div className="container mx-auto px-6 lg:px-12 pt-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                            <div>
                                <span className="inline-flex items-center rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white/90 mb-5">
                                    Esports League Software
                                </span>
                                <h1 className="font-bebas text-white uppercase text-[clamp(38px,5.5vw,68px)] leading-none font-bold tracking-wide mb-5">
                                    Run online seasons and splits without a pinned spreadsheet
                                </h1>
                                <p className="font-inter text-white/85 text-base lg:text-xl max-w-2xl leading-relaxed mb-8">
                                    ScorePal helps esports organisers schedule a split, record best-of series map by map,
                                    run Swiss rounds when the field is large, and publish standings every team can see —
                                    across as many divisions as you run.
                                </p>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-[#EDEEF4] text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-white transition">
                                        <span className="font-inter text-base font-medium">Get started - it's free</span>
                                        <div className="bg-[#082F8A] rounded-full p-1">
                                            <ArrowUpRightIcon className="text-white" size={28} />
                                        </div>
                                    </Link>
                                    <Link to={"/esports-tournament-software/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                        Running a Tournament?
                                    </Link>
                                </div>
                            </div>

                            {/* Live standings card */}
                            <div className="bg-white/5 border border-white/10 rounded-3xl p-6 lg:p-8 backdrop-blur-sm">
                                <h3 className="font-bebas text-white text-2xl font-bold tracking-wide mb-2">Live split standings</h3>
                                <p className="font-inter text-white/70 text-sm mb-5">Records and map differential update as each series is submitted.</p>
                                <div className="rounded-2xl bg-white/10 p-3">
                                    <div className="grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 pb-2 border-b border-white/15 font-inter text-white/50 text-xs uppercase tracking-wide">
                                        <span>#</span><span>Team</span><span className="text-center">W-L</span><span className="text-right">Maps</span>
                                    </div>
                                    {[
                                        {pos: 1, team: "Nova Collective", rec: "7-1", maps: "+11"},
                                        {pos: 2, team: "Iron Vanguard", rec: "6-2", maps: "+7"},
                                        {pos: 3, team: "Static Legion", rec: "3-5", maps: "-3"},
                                        {pos: 4, team: "Pixel Riot", rec: "1-7", maps: "-12"},
                                    ].map((row, i, arr) => (
                                        <div key={row.pos} className={`grid grid-cols-[28px_1fr_48px_44px] items-center gap-2 py-3 ${i < arr.length - 1 ? "border-b border-white/10" : ""}`}>
                                            <span className="font-inter text-white/60 text-sm">{row.pos}</span>
                                            <strong className="font-inter text-white text-sm">{row.team}</strong>
                                            <span className="font-inter text-white/70 text-sm text-center">{row.rec}</span>
                                            <strong className="font-inter text-white text-sm text-right">{row.maps}</strong>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section id="features" className="py-16 lg:py-24 bg-white scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Everything you need to run an esports league
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        Replace the pinned schedule, the results screenshots and the spreadsheet only one admin can edit.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {FEATURES.map((f) => (
                            <div key={f.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <div className="w-11 h-11 rounded-xl bg-[#EDEEF4] text-[#082F8A] flex items-center justify-center font-bold mb-4">{f.code}</div>
                                <h3 className="font-inter text-[#1a1a2e] font-semibold text-lg mb-2">{f.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* League types */}
            <section id="league-types" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Support for every type of esports competition
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        A full round-robin split, Swiss rounds for a large open field, or a cup with a knockout bracket — ScorePal handles the structure.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {LEAGUE_TYPES.map((f) => (
                            <Link key={f.name} to={f.to as any} className="group bg-white border border-[#e5e7eb] rounded-2xl p-5 hover:-translate-y-1 hover:border-[#082F8A]/25 transition">
                                <div className="font-inter font-bold text-[#1a1a2e] group-hover:text-[#082F8A]">{f.name}</div>
                                <div className="font-inter text-[#1a1a2e]/60 text-sm mt-1">{f.sub}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Season + mock app */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                    <div>
                        <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-4">
                            Built for competition that lives online
                        </h2>
                        <p className="font-inter text-[#1a1a2e]/70 text-base leading-relaxed mb-6">
                            There is no venue to book, but there is still a schedule to hold, results to collect and a
                            table teams will scrutinise. ScorePal keeps all three in one place instead of spread across
                            a chat server.
                        </p>
                        <div className="space-y-4">
                            {BENEFITS.map((b) => (
                                <div key={b.title} className="flex gap-3">
                                    <CheckBadgeIcon size={24} />
                                    <div>
                                        <strong className="font-inter text-[#1a1a2e]">{b.title}</strong>
                                        <p className="font-inter text-[#1a1a2e]/70 text-sm">{b.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mock app */}
                    <div className="rounded-3xl bg-linear-to-br from-[#eef4ff] to-white border border-[#e5e7eb] p-6 lg:p-8 shadow-sm">
                        <div className="bg-white border border-[#e5e7eb] rounded-2xl p-5">
                            {[
                                {l: "Spring Split — Week 4", r: "In progress", badge: "live"},
                                {l: "Next series", r: "Nova Collective vs Static Legion"},
                                {l: "Scheduled", r: "Sunday 19:00 · Bo3"},
                                {l: "Last series", r: "2-1 (13-9, 8-13, 13-11)"},
                                {l: "Top of the table", r: "Nova Collective", badge: "gold"},
                            ].map((row, i, arr) => (
                                <div key={row.l} className={`flex items-center justify-between gap-3 py-3.5 font-inter text-sm ${i < arr.length - 1 ? "border-b border-[#eef0f4]" : ""}`}>
                                    <span className="text-[#1a1a2e]/70">{row.l}</span>
                                    {row.badge === "live"
                                        ? <span className="rounded-full bg-[#ecfdf3] text-[#12b76a] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                        : row.badge === "gold"
                                            ? <span className="rounded-full bg-[#fffbeb] text-[#b54708] px-2.5 py-1 text-xs font-bold">{row.r}</span>
                                            : <strong className="text-[#1a1a2e] text-right">{row.r}</strong>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Comparison */}
            <section id="comparison" className="py-16 lg:py-24 bg-[#EDEEF4] scroll-mt-24">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        ScorePal vs spreadsheets for esports leagues
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-8">
                        Running a split out of a chat server works until results go missing, pairings need redoing and every team has a different idea of the table.
                    </p>
                    <div className="overflow-x-auto">
                        <div className="min-w-[680px] border border-[#d9dce7] rounded-3xl overflow-hidden bg-white shadow-sm">
                            <div className="grid grid-cols-3 bg-[#F6F7FB] font-bebas text-lg">
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Need</div>
                                <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]">Spreadsheets</div>
                                <div className="p-5 text-[#082F8A]">ScorePal</div>
                            </div>
                            {COMPARE.map((row, i) => (
                                <div key={row.need} className={`grid grid-cols-3 border-t border-[#d9dce7] font-inter text-[#1a1a2e] ${i % 2 ? "bg-[#FAFBFD]" : ""}`}>
                                    <div className="p-5 border-r border-[#d9dce7] font-semibold">{row.need}</div>
                                    <div className="p-5 border-r border-[#d9dce7] text-[#1a1a2e]/70">{row.manual}</div>
                                    <div className="p-5 font-medium">{row.scorepal}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Use cases */}
            <section className="py-16 lg:py-24 bg-white">
                <div className="container mx-auto px-6 lg:px-12">
                    <h2 className="font-bebas text-[#1a1a2e] uppercase text-[clamp(28px,4vw,42px)] leading-tight font-bold tracking-wide mb-3">
                        Built for communities, universities and amateur circuits
                    </h2>
                    <p className="font-inter text-[#1a1a2e]/70 text-base max-w-2xl mb-10">
                        From a community season to a multi-tier circuit, ScorePal keeps schedules, series results and standings in order.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {USE_CASES.map((u) => (
                            <div key={u.title} className="bg-white border border-[#e5e7eb] rounded-2xl p-6 shadow-sm">
                                <h3 className="font-inter text-[#082F8A] font-semibold text-base mb-2">{u.title}</h3>
                                <p className="font-inter text-[#1a1a2e]/70 text-sm leading-relaxed">{u.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA card → tournament software */}
            <section className="container mx-auto px-6 lg:px-12 py-8 lg:py-12">
                <div className="rounded-3xl bg-linear-to-br from-[#0a1530] via-[#0d1b3d] to-[#082F8A] p-8 lg:p-12 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-12 items-center">
                    <div>
                        <h2 className="font-bebas text-white uppercase text-[clamp(26px,3.5vw,40px)] leading-tight font-bold tracking-wide mb-4">
                            Running a one-off esports cup?
                        </h2>
                        <p className="font-inter text-white/80 text-base leading-relaxed mb-6">
                            This page is about seasons and splits. If you are running a weekend cup with a group stage
                            and a knockout bracket, see our esports tournament software page.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to={"/esports-tournament-software/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                                <span className="font-inter text-base font-medium">View Tournament Software</span>
                                <div className="bg-[#082F8A] rounded-full p-1">
                                    <ArrowUpRightIcon className="text-white" size={28} />
                                </div>
                            </Link>
                            <Link to={"/signup/player/" as any} className="rounded-full border border-white/40 px-6 py-3 text-base font-semibold text-white transition hover:bg-white/10">
                                Get started - it's free
                            </Link>
                        </div>
                    </div>
                    <div className="bg-white/10 rounded-2xl p-6">
                        <h3 className="font-inter text-white font-semibold mb-3">Popular tournament features</h3>
                        <ul className="list-disc pl-5 space-y-1.5 font-inter text-sm text-white/85">
                            <li>Group stages</li>
                            <li>Single & double elimination</li>
                            <li>Swiss rounds</li>
                            <li>Seeding</li>
                            <li>Best-of series</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="container mx-auto px-6 lg:px-12 pb-8 lg:pb-12">
                <div className="bg-[#082F8A] rounded-3xl px-8 py-16 lg:px-16 lg:py-20 text-center">
                    <h2 className="font-bebas text-white uppercase text-[clamp(32px,5vw,52px)] leading-tight font-bold tracking-wide mb-6">
                        Run your next esports league with ScorePal
                    </h2>
                    <p className="font-inter text-white/90 text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                        Seasons and splits, best-of series, Swiss rounds, rosters, divisions and live standings — esports league software built for organisers.
                    </p>
                    <div className="flex flex-wrap justify-center gap-2.5 mb-10">
                        {["Seasons & Splits", "Best-of Series", "Swiss Rounds", "Rosters", "Divisions", "Live Standings"].map((pill) => (
                            <span key={pill} className="rounded-full bg-white/10 px-4 py-2 font-inter text-sm font-medium text-white/90">{pill}</span>
                        ))}
                    </div>
                    <div className="flex justify-center">
                        <Link to={"/signup/player/" as any} className="inline-flex items-center gap-2 bg-white text-[#082F8A] rounded-full pl-6 pr-1 py-1 font-semibold hover:bg-gray-100 transition">
                            <span className="font-inter text-base font-medium">Get started - it's free</span>
                            <div className="bg-[#082F8A] rounded-full p-1">
                                <ArrowUpRightIcon className="text-white" size={28} />
                            </div>
                        </Link>
                    </div>
                </div>
            </section>

            <FaqComponent title={"Esports League Software FAQs"} faqs={FAQS} />
            <FooterSection />
        </div>
    );
}
